import { useEffect, useId, useState, useContext } from 'react';
import { useGlobalHydrationActions } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';
import { WaitContext } from '@atlassian/react-loosely-lazy/lazy-wait/context';

type Params = {
	// True if the suspense's fallback or children component has completed rendering
	isSubtreeRendered: boolean;
	name: string;
};

/**
 * This component is rendered at all suspense boundaries (placeholders), to be able to know when
 * hydration has completed globally - when every subtree has rendered.
 */
export const useHydrationSubtreeCompleter = ({
	isSubtreeRendered,
	name: unstableName,
}: Params): string => {
	const id = useId();
	const [name] = useState(unstableName);
	const { addPendingSubtree, completePendingSubtree } = useGlobalHydrationActions();

	const waitContext = useContext(WaitContext);

	useEffect(() => {
		if (waitContext?.currentValue() === 0) {
			// no effect if we are inside a LazyWait boundary
			return;
		}

		if (isSubtreeRendered) {
			completePendingSubtree(id);
		} else {
			addPendingSubtree(id, name);
		}
	}, [isSubtreeRendered, id, name, completePendingSubtree, addPendingSubtree, waitContext]);

	// Exists to ensure that we complete a subtree if it unmounts before `isSubtreeRendered` flips to true
	useEffect(() => {
		return () => {
			completePendingSubtree(id);
		};
	}, [id, completePendingSubtree]);

	// prevent dead code removal
	return id;
};
